import LeadFilters from "./components/LeadFilters/LeadFilters";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "src/context/AuthContext";
import LeadDrawer from "./LeadDrawer";
import CustomerWorkspacePage from "src/common/customerSharing/CustomerWorkspacePage";
import LeadsHeader from "./components/Leads/LeadsHeader";
import LeadsTable from "./components/Leads/LeadsTable";
import LeadsBoard from "./components/Leads/LeadsBoard";
import { QUICK_ADD_EVENT } from "src/features/Stage/shared/CommandPalette";
import LeadViews from "./components/LeadFilters/LeadViews";
import ImportLeadsModal from "./components/LeadImport/ImportLeadsModal";
import { buildLeadQuery, countActiveFilters } from "./components/LeadFilters/filterUtils";
import { DATE_PRESETS, DEFAULT_FILTERS } from "./components/LeadFilters/constants";
import { clearLeads, getLeadsPage, getPipelines } from "./CachedData";
import "src/stage/stage.css";
import "src/stage/crm-stage.css";
import "./leads-stage.css";
import { exportLeads, getLeadFields, getLeadSchema } from "./services/leadFieldService";

const initialPagination = {
    total: 0,
    currentPage: 1,
    lastPage: 1,
    perPage: 50,
};

export default function Leads() {
    const { currentUser } = useContext(AuthContext);
    const location = useLocation();
    const navigate = useNavigate();
    const linkedParams = new URLSearchParams(location.search);
    const linkedLead = linkedParams.get("lead");
    const linkedWorkspace = linkedParams.get("workspace");
    const workspaceId =
        localStorage.getItem("autoappy_current_workspace_id") ||
        currentUser?.local_workspace_id ||
        currentUser?.current_workspace?.id ||
        "";
    const [selectedPipeline, setSelectedPipeline] = useState(
        () => localStorage.getItem("selectedPipelineId") || ""
    );
    const [leads, setLeads] = useState([]);
    const [leadsLoading, setLeadsLoading] = useState(Boolean(workspaceId));
    const [leadsError, setLeadsError] = useState("");
    const [pagination, setPagination] = useState(initialPagination);
    const [viewCounts, setViewCounts] = useState({});
    const [sort, setSortState] = useState(null);
    const [showImportModal, setShowImportModal] = useState(false);
    const [showLeadDrawer, setShowLeadDrawer] = useState(false);
    const [selectedLead, setSelectedLead] = useState(null);
    const [filters, setFiltersState] = useState(DEFAULT_FILTERS);
    const [search, setSearchState] = useState("");
    const [customFields, setCustomFields] = useState([]);
    const [leadSchema, setLeadSchema] = useState(null);
    const [schemaError, setSchemaError] = useState("");
    const [exporting, setExporting] = useState(false);
    const [pipelineName, setPipelineName] = useState("");
    // Board or list, remembered per browser. The board suits most small teams.
    const [layout, setLayoutState] = useState(() => { try { return localStorage.getItem("crm.leads.layout") || "board"; } catch { return "board"; } });
    const [boardRefresh, setBoardRefresh] = useState(0);
    const setLayout = useCallback((value) => { setLayoutState(value); try { localStorage.setItem("crm.leads.layout", value); } catch { /* private mode */ } }, []);
    const addLead = useCallback(() => window.dispatchEvent(new Event(QUICK_ADD_EVENT)), []);

    useEffect(() => {
        let active = true;
        getPipelines(workspaceId)
            .then((rows) => {
                const list = Array.isArray(rows) ? rows : rows?.data || [];
                if (active) setPipelineName(list.find((pipeline) => String(pipeline.id) === String(selectedPipeline))?.name || "");
            })
            .catch(() => { if (active) setPipelineName(""); });
        return () => { active = false; };
    }, [selectedPipeline, workspaceId]);

    const customerView = linkedParams.get("view") === "customer";
    const validCustomerView = customerView && /^\d+$/.test(linkedLead || "") && linkedWorkspace === workspaceId && Boolean(workspaceId);
    const returnSearch = useRef(null);
    const linkedWorkspaceMismatch = Boolean(linkedLead && linkedWorkspace !== workspaceId);
    useEffect(() => {
        if (customerView) { setShowLeadDrawer(false); return; }
        if (!linkedLead) return;
        if (linkedWorkspace !== workspaceId || !/^\d+$/.test(linkedLead)) { setShowLeadDrawer(false); return; }
        setSelectedLead(linkedLead);
        setShowLeadDrawer(true);
    }, [linkedLead, linkedWorkspace, workspaceId, customerView]);

    const tableRef = useRef(null);
    const leadRequestRef = useRef(0);
    const selectedPipelineRef = useRef(selectedPipeline);
    const activeFilterCount = useMemo(() => countActiveFilters(filters), [filters]);
    const filterQuery = useMemo(
        () => buildLeadQuery(filters, search, sort),
        [filters, search, sort]
    );

    useEffect(() => {
        if (!workspaceId) return;
        let active = true;
        setLeadSchema(null); setSchemaError(""); setCustomFields([]);
        Promise.all([getLeadFields(workspaceId, { pipelineId: selectedPipeline }), getLeadSchema(workspaceId, selectedPipeline)])
            .then(([fields, schema]) => { if (active) { setCustomFields(fields); setLeadSchema(schema); } })
            .catch(() => { if (active) setSchemaError("Couldn’t load pipeline columns. Refresh this page to retry."); });
        return () => { active = false; };
    }, [selectedPipeline, workspaceId]);

    const scrollToTable = useCallback(() => {
        tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, []);

    const setSearch = useCallback((value) => {
        setSearchState(value);
        setPagination((current) => ({ ...current, currentPage: 1 }));
    }, []);

    const setFilters = useCallback((value) => {
        setFiltersState((current) => typeof value === "function" ? value(current) : value);
        setPagination((current) => ({ ...current, currentPage: 1 }));
    }, []);

    const setSort = useCallback((value) => {
        setSortState((current) => typeof value === "function" ? value(current) : value);
        setPagination((current) => ({ ...current, currentPage: 1 }));
    }, []);

    const selectPipeline = useCallback((pipelineId) => {
        if (!pipelineId || pipelineId === selectedPipelineRef.current) return;
        selectedPipelineRef.current = pipelineId;
        localStorage.setItem("selectedPipelineId", pipelineId);
        setSearchState("");
        setFiltersState(structuredClone(DEFAULT_FILTERS));
        setSortState(null);
        setPagination((current) => ({ ...current, currentPage: 1 }));
        setSelectedPipeline(pipelineId);
    }, []);

    const fetchLeads = useCallback(async (pageOverride = null) => {
        if (!workspaceId) {
            setLeadsLoading(false);
            return;
        }

        const requestId = ++leadRequestRef.current;
        const requestedPage = pageOverride || pagination.currentPage;
        setLeadsLoading(true);
        setLeadsError("");

        try {
            const response = await getLeadsPage({
                ...filterQuery,
                pipeline_id: selectedPipeline || undefined,
                page: requestedPage,
                per_page: pagination.perPage,
            }, workspaceId);

            if (requestId !== leadRequestRef.current) return;

            const nextLeads = Array.isArray(response) ? response : response?.data || [];
            const meta = response?.meta || {};
            setLeads(nextLeads);
            setViewCounts(response?.view_counts || {});
            setPagination((current) => ({
                ...current,
                total: meta.total ?? nextLeads.length,
                currentPage: meta.current_page ?? requestedPage,
                lastPage: meta.last_page ?? 1,
                perPage: meta.per_page ?? current.perPage,
            }));
        } catch (error) {
            if (requestId !== leadRequestRef.current) return;

            console.error(error);
            setLeads([]);
            setViewCounts({});
            setPagination((current) => ({ ...current, total: 0, lastPage: 1 }));
            setLeadsError(
                error.response?.data?.message ||
                "We couldn't load the leads for this pipeline."
            );
        } finally {
            if (requestId === leadRequestRef.current) {
                setLeadsLoading(false);
            }
        }
    }, [filterQuery, pagination.currentPage, pagination.perPage, selectedPipeline, workspaceId]);

    useEffect(() => {
        const delay = search.trim() ? 250 : 0;
        const timeout = window.setTimeout(() => fetchLeads(), delay);
        return () => window.clearTimeout(timeout);
    }, [fetchLeads, search]);

    useEffect(() => {
        const handleLeadsReset = () => {
            clearLeads();
            fetchLeads(1);
        };

        window.addEventListener("leads-reset", handleLeadsReset);
        return () => window.removeEventListener("leads-reset", handleLeadsReset);
    }, [fetchLeads]);

    const refreshLeads = useCallback(() => {
        clearLeads();
        setPagination((current) => ({ ...current, currentPage: 1 }));
        setBoardRefresh((value) => value + 1);
        fetchLeads(1);
    }, [fetchLeads]);

    const handleOverviewViewChange = useCallback((view) => {
        const nextFilters = structuredClone(DEFAULT_FILTERS);

        if (view?.type === "period" && view.period?.from && view.period?.to) {
            nextFilters.createdDate = {
                preset: DATE_PRESETS.CUSTOM_RANGE,
                customRange: {
                    startDate: view.period.from,
                    endDate: view.period.to,
                },
            };
        }

        if (view?.type === "stage" && view.stageId) {
            nextFilters.stageIds = [Number(view.stageId)];
        }

        if (view?.type === "queue") {
            nextFilters.leadIds = (view.leadIds || []).map((leadId) => Number(leadId));
            nextFilters.queueKey = view.key;
            nextFilters.queueLabel = view.label;
        }

        setSearch("");
        setFilters(nextFilters);
        window.requestAnimationFrame(scrollToTable);
    }, [scrollToTable, setFilters, setSearch]);

    // Home and Insights open the list on a pipeline with a queue, stage or period
    // already applied (AUTO-CRM-32): navigate('/crm', { state: { leadView } }).
    const leadView = location.state?.leadView || (location.state?.leadQueue && { type: "queue", ...location.state.leadQueue });
    useEffect(() => {
        if (location.state?.openImport) {
            setShowImportModal(true);
            navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null });
            return;
        }
        if (location.state?.pipelineId && !leadView) {
            selectPipeline(String(location.state.pipelineId));
            navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null });
            return;
        }
        if (!leadView) return;
        // A queue is a list of leads; the list shows it best.
        if (leadView.type === "queue") setLayout("list");
        if (leadView.pipelineId && leadView.pipelineId !== selectedPipelineRef.current) {
            selectedPipelineRef.current = leadView.pipelineId;
            localStorage.setItem("selectedPipelineId", leadView.pipelineId);
            setSelectedPipeline(leadView.pipelineId);
        }
        handleOverviewViewChange(leadView);
        navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null });
    }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

    const hasAnyLeads = (viewCounts.all ?? pagination.total) > 0;

    const handleExport = useCallback(async () => {
        if (!workspaceId || exporting) return;
        setExporting(true);
        try {
            const response = await exportLeads(workspaceId, {
                ...filterQuery,
                pipeline_id: selectedPipeline || undefined,
            });
            const url = URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = url;
            link.download = `crm-leads-${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error(error);
            window.alert("Unable to export the current lead view.");
        } finally {
            setExporting(false);
        }
    }, [exporting, filterQuery, selectedPipeline, workspaceId]);

    const openCustomer = id => {
        returnSearch.current = location.search;
        const params = new URLSearchParams(location.search);
        params.set('lead', String(id)); params.set('workspace', workspaceId); params.set('view', 'customer');
        setShowLeadDrawer(false);
        navigate({ pathname: '/crm', search: `?${params}` });
    };
    const closeCustomer = details => {
        const params = new URLSearchParams(returnSearch.current ?? location.search);
        params.delete('view'); params.delete('lead');
        if (details) { params.set('lead', linkedLead); params.set('workspace', workspaceId); }
        setSelectedLead(details ? linkedLead : null);
        setShowLeadDrawer(Boolean(details));
        navigate({ pathname: '/crm', search: params.toString() ? `?${params}` : '' });
    };
    return (
        <main className="ws crm-ws crm-ws--wide crm-leads-body crm-leads-stage">
            {validCustomerView && <CustomerWorkspacePage key={`${workspaceId}:${linkedLead}`} leadId={linkedLead} workspaceId={workspaceId} onBack={() => closeCustomer(false)} onDetails={() => closeCustomer(true)} onSaved={refreshLeads} />}
            {customerView && !validCustomerView && <div className="crm-customer-page-state ws-notice"><p role="alert">This customer link is invalid or belongs to another workspace. Select the correct workspace to continue.</p><button type="button" className="ws-btn ws-btn--quiet ws-btn--sm" onClick={() => closeCustomer(false)}>Back to leads</button></div>}
            <div className="ws-page" hidden={customerView}>
            {linkedWorkspaceMismatch && <p role="alert" className="ws-notice">This customer link belongs to another workspace. Select that workspace to open the exact opportunity.</p>}
            <LeadsHeader
                workspaceId={workspaceId}
                pipelineName={pipelineName}
                total={pagination.total}
                loading={leadsLoading}
                onAddLead={addLead}
                onImportLeads={() => setShowImportModal(true)}
                onExportLeads={handleExport}
                exporting={exporting}
                onTableClick={scrollToTable}
            />

            <LeadViews
                viewCounts={viewCounts}
                workspaceId={workspaceId}
                filters={filters}
                setFilters={setFilters}
                activeFilterCount={activeFilterCount}
                pipelineId={selectedPipeline}
                onPipelineChange={selectPipeline}
                onViewSelected={scrollToTable}
                layout={layout}
                onLayoutChange={setLayout}
            />

            <div ref={tableRef} className="crm-leads-table-wrap">
                {layout === "board" ? (
                <LeadsBoard
                    workspaceId={workspaceId}
                    pipelineId={selectedPipeline}
                    query={filterQuery}
                    customFields={customFields}
                    refreshKey={boardRefresh}
                    toolbar={<LeadFilters search={search} setSearch={setSearch} activeFilterCount={activeFilterCount} filters={filters} setFilters={setFilters} workspaceId={workspaceId} pipelineId={selectedPipeline} customFields={[...(leadSchema?.starter_fields || []).map((field) => ({ ...field, key: `starter:${field.key}`, field_type: "text", is_filterable: true })), ...customFields]} />}
                    onOpenLead={(id) => { setSelectedLead(id); setShowLeadDrawer(true); }}
                    onEmptyAdd={addLead}
                    onEmptyImport={() => setShowImportModal(true)}
                />
                ) : (
                <LeadsTable
                    toolbar={<LeadFilters search={search} setSearch={setSearch} activeFilterCount={activeFilterCount} filters={filters} setFilters={setFilters} workspaceId={workspaceId} pipelineId={selectedPipeline} customFields={[...(leadSchema?.starter_fields || []).map((field) => ({ ...field, key: `starter:${field.key}`, field_type: "text", is_filterable: true })), ...customFields]} />}
                    leads={leads}
                    loading={leadsLoading || (!leadSchema && !schemaError)}
                    error={schemaError || leadsError}
                    starterFields={leadSchema?.starter_fields || []}
                    pipelineId={selectedPipeline}
                    workspaceId={workspaceId}
                    onLeadClick={(id) => {
                        setSelectedLead(id);
                        setShowLeadDrawer(true);
                    }}
                    sort={sort}
                    setSort={setSort}
                    pagination={pagination}
                    search={search}
                    hasAnyLeads={hasAnyLeads}
                    onAddLead={addLead}
                    onImportLeads={() => setShowImportModal(true)}
                    onPageChange={(page) => setPagination((current) => ({ ...current, currentPage: page }))}
                    onLimitChange={(perPage) => setPagination((current) => ({ ...current, perPage, currentPage: 1 }))}
                    onDeleted={refreshLeads}
                    onRetry={() => {
                        clearLeads();
                        fetchLeads();
                    }}
                    customFields={customFields}
                />
                )}
            </div>
            </div>

            {showImportModal && (
                <ImportLeadsModal
                    starterFields={leadSchema?.starter_fields}
                    schemaError={schemaError}
                    workspaceId={workspaceId}
                    pipelineId={selectedPipeline}
                    customFields={customFields}
                    onClose={() => setShowImportModal(false)}
                    onImported={refreshLeads}
                />
            )}

            <LeadDrawer
                key={workspaceId}
                onDeleted={refreshLeads}
                open={!customerView && showLeadDrawer}
                onOpenCustomer={openCustomer}
                leadId={selectedLead}
                workspaceId={workspaceId}
                onClose={() => setShowLeadDrawer(false)}
            />
        </main>
    );
}
